import React, { useEffect, useMemo, useState } from "react";
import {
  Blocks,
  ShieldCheck,
  AlertTriangle,
  Hash,
  Link2,
  Copy,
  Check,
  RefreshCw,
  ChevronRight,
  Layers,
  GitBranch,
  Lock,
  Sparkles,
  ScrollText,
  Search,
  Anchor,
  Database,
  Code2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import OriginBadge from "../components/sentinel/OriginBadge";
import DisputeButton from "../components/sentinel/DisputeButton";
import {
  listTruthBlocks,
  getTruthBlock,
  getTruthBlockProof,
  verifyTruthChain,
  getTruthBlocksPending,
  sealTruthBlock,
  getTruthCompilerStats,
  truthCompilerRecall,
  truthCompilerBackfill,
} from "../lib/api";

// SHA-256 hex in-browser — no Sentinel secret required to verify a
// Merkle proof. The verifier reproduces the exact same step Bitcoin
// nodes use: hash(running || sibling) or hash(sibling || running).
async function sha256Hex(text) {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function shortHash(h, n = 12) {
  if (!h) return "—";
  if (h.length <= n * 2 + 1) return h;
  return `${h.slice(0, n)}…${h.slice(-6)}`;
}

function CopyHash({ value, label = "hash", testid }) {
  const [done, setDone] = useState(false);
  if (!value) return <span className="text-zinc-600 font-mono text-[11px]">—</span>;
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          toast.success(`${label} copied`);
          setTimeout(() => setDone(false), 1200);
        } catch {
          toast.error("Clipboard unavailable");
        }
      }}
      data-testid={testid}
      title={`Copy full ${label}`}
      className="group font-mono text-[11px] text-zinc-300 hover:text-emerald-300 inline-flex items-center gap-1.5 border border-zinc-800 hover:border-emerald-500/50 px-1.5 py-0.5 transition-colors"
    >
      {done ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3 opacity-50 group-hover:opacity-100" />}
      {shortHash(value)}
    </button>
  );
}

function DistributionBar({ dist = {} }) {
  const total = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
  const order = [
    "fact", "web_cited", "fiction", "opinion", "bounty_open",
    "contested", "theory", "refuted", "uncited",
  ];
  const palette = {
    fact:        "bg-emerald-500/70",
    web_cited:   "bg-sky-500/70",
    fiction:     "bg-rose-500/70",
    opinion:     "bg-amber-500/70",
    bounty_open: "bg-violet-500/60",
    contested:   "bg-amber-500/70",
    theory:      "bg-violet-500/60",
    refuted:     "bg-rose-500/70",
    uncited:     "bg-zinc-700/60",
  };
  return (
    <div className="flex h-2 w-full overflow-hidden border border-zinc-800">
      {order.map((k) => {
        const v = dist[k] || 0;
        if (!v) return null;
        const pct = (v / total) * 100;
        return (
          <div
            key={k}
            className={palette[k]}
            style={{ width: `${pct}%` }}
            title={`${k}: ${v}`}
          />
        );
      })}
    </div>
  );
}

function ProofVerifier({ block, leaf, proof, expectedRoot }) {
  // Recompute root client-side using WebCrypto. Proves the auditor
  // doesn't have to trust the server's `self_verified` flag.
  const [computed, setComputed] = useState(null);
  const [status, setStatus] = useState("computing");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let running = leaf.leaf_hash;
        for (const step of proof) {
          const sib = step.hash;
          running = step.position === "right"
            ? await sha256Hex(running + sib)
            : await sha256Hex(sib + running);
        }
        if (cancelled) return;
        setComputed(running);
        setStatus(running === expectedRoot ? "verified" : "mismatch");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [leaf.leaf_hash, proof, expectedRoot]);

  const colors = {
    verified:  { border: "border-emerald-500/60", bg: "bg-emerald-500/5",  text: "text-emerald-300", icon: ShieldCheck, label: "Verified · root matches" },
    mismatch:  { border: "border-rose-500/60",    bg: "bg-rose-500/10",    text: "text-rose-300",    icon: AlertTriangle, label: "MISMATCH · tampered" },
    error:     { border: "border-amber-500/60",   bg: "bg-amber-500/10",   text: "text-amber-300",   icon: AlertTriangle, label: "Verifier error" },
    computing: { border: "border-zinc-700",       bg: "bg-zinc-900",       text: "text-zinc-400",    icon: RefreshCw,   label: "Computing…" },
  }[status];
  const Icon = colors.icon;

  return (
    <div className={`mt-3 border ${colors.border} ${colors.bg} p-3`} data-testid="proof-verifier-result">
      <div className={`text-[10px] uppercase tracking-[0.25em] ${colors.text} flex items-center gap-2`}>
        <Icon className={`h-3.5 w-3.5 ${status === "computing" ? "animate-spin" : ""}`} />
        {colors.label}
      </div>
      <div className="mt-2 grid sm:grid-cols-2 gap-3 text-[10.5px] font-mono">
        <div>
          <div className="text-zinc-500 uppercase tracking-[0.25em] mb-0.5">Recomputed root</div>
          <div className="text-zinc-200 break-all">{computed || "—"}</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-[0.25em] mb-0.5">Block&apos;s sealed root</div>
          <div className="text-zinc-200 break-all">{expectedRoot}</div>
        </div>
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {proof.length} sibling hash{proof.length === 1 ? "" : "es"} walked · SHA-256 ·{" "}
        verified entirely in your browser
      </div>
    </div>
  );
}

function BlockCard({ block, onOpen }) {
  return (
    <article
      className={`group border ${
        block.is_genesis
          ? "border-amber-500/60 bg-amber-500/[0.04] hover:border-amber-400"
          : "border-zinc-800 bg-[#0b0b0d] hover:border-emerald-500/60 hover:bg-zinc-900/40"
      } transition-colors cursor-pointer`}
      onClick={() => onOpen(block.id)}
      data-testid={`truth-block-${block.height}`}
    >
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`shrink-0 h-12 w-12 border flex flex-col items-center justify-center ${
            block.is_genesis ? "border-amber-500/60 text-amber-300" : "border-emerald-500/40 text-emerald-300"
          }`}>
            <Layers className="h-3.5 w-3.5" />
            <div className="font-mono text-[11px] mt-0.5">#{block.height}</div>
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              {block.is_genesis && (
                <span
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.3em] border border-amber-500/60 bg-amber-500/10 text-amber-300 font-mono drop-shadow-[0_0_6px_rgba(251,191,36,0.4)]"
                  data-testid={`genesis-badge-${block.height}`}
                  title={block.genesis_memorial || "Genesis Block"}
                >
                  △ Genesis
                </span>
              )}
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-emerald-500/40 text-emerald-300">
                {block.leaf_count} fact{block.leaf_count === 1 ? "" : "s"}
              </span>
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-sky-500/40 text-sky-300">
                {block.depth_of_truth} DoT
              </span>
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-zinc-700 text-zinc-400 font-mono">
                {(block.size_bytes / 1024).toFixed(1)} KiB
              </span>
            </div>
            <div className="mt-1.5 max-w-[34rem]">
              <DistributionBar dist={block.epistemic_distribution || {}} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              <span>root · <span className="text-zinc-300 normal-case">{shortHash(block.merkle_root)}</span></span>
              <span>prev · <span className="text-zinc-300 normal-case">{shortHash(block.previous_hash)}</span></span>
              {block.sealed_at && (
                <span className="normal-case">{new Date(block.sealed_at).toISOString().slice(0, 19).replace("T", " ")}Z</span>
              )}
            </div>
          </div>
        </div>
        <ChevronRight className="shrink-0 h-4 w-4 text-zinc-600 group-hover:text-emerald-400 transition-colors" />
      </div>
    </article>
  );
}

function BlockDetail({ blockId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openLeaf, setOpenLeaf] = useState(null);
  const [proof, setProof] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTruthBlock(blockId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [blockId]);

  const block = data?.block;
  const verification = data?.verification;

  const loadProof = async (citationId) => {
    setOpenLeaf(citationId);
    setProof(null);
    setProofLoading(true);
    try {
      const res = await getTruthBlockProof(blockId, citationId);
      setProof(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Proof fetch failed");
    } finally {
      setProofLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid="truth-block-detail">
      <div className="min-h-full px-3 py-6 sm:px-6 sm:py-10">
        <div className="max-w-4xl mx-auto bg-[#0a0a0c] border border-emerald-500/30 shadow-[0_0_60px_-20px_rgba(16,185,129,0.4)]">
          <header className="flex items-start justify-between gap-4 p-5 border-b border-zinc-900">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400 mb-1">
                △ Proof-of-Verification · sealed block
              </div>
              <h2 className="font-heading text-2xl tracking-[0.1em] text-zinc-100">
                Truth Block #{block?.height ?? "…"}
              </h2>
            </div>
            <button
              onClick={onClose}
              data-testid="close-block-detail"
              className="text-zinc-400 hover:text-zinc-100 border border-zinc-800 hover:border-zinc-600 px-3 py-1 text-[11px] uppercase tracking-[0.2em]"
            >
              Close
            </button>
          </header>

          {loading && (
            <div className="p-12 text-center font-mono uppercase tracking-[0.25em] text-zinc-500" data-testid="block-detail-loading">
              loading block…
            </div>
          )}
          {error && (
            <div className="m-5 p-4 border border-rose-500/40 bg-rose-500/5 text-rose-300 font-mono text-sm" data-testid="block-detail-error">
              {String(error)}
            </div>
          )}

          {block && (
            <div className="p-5 space-y-6">
              {/* Header card */}
              <div className="grid sm:grid-cols-2 gap-3 text-[11px]">
                <div className="border border-zinc-800 p-3 bg-[#08080a]">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Merkle Root</div>
                  <CopyHash value={block.merkle_root} label="Merkle root" testid="detail-merkle-root" />
                </div>
                <div className="border border-zinc-800 p-3 bg-[#08080a]">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Block Hash</div>
                  <CopyHash value={block.block_hash} label="block hash" testid="detail-block-hash" />
                </div>
                <div className="border border-zinc-800 p-3 bg-[#08080a]">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Previous Hash</div>
                  {block.previous_hash
                    ? <CopyHash value={block.previous_hash} label="previous hash" />
                    : <span className="text-emerald-400 text-[10px] uppercase tracking-[0.25em]">Genesis · ∅</span>}
                </div>
                <div className="border border-zinc-800 p-3 bg-[#08080a]">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">HMAC Signature</div>
                  <CopyHash value={block.seal_signature_sha256} label="seal signature" />
                </div>
              </div>

              {/* Verification badge */}
              {verification && (
                <div className={`border ${verification.valid ? "border-emerald-500/50 bg-emerald-500/5" : "border-rose-500/50 bg-rose-500/10"} p-3 flex items-center gap-2`}>
                  {verification.valid ? <ShieldCheck className="h-4 w-4 text-emerald-300" /> : <AlertTriangle className="h-4 w-4 text-rose-300" />}
                  <span className={`text-[11px] uppercase tracking-[0.25em] ${verification.valid ? "text-emerald-300" : "text-rose-300"}`}>
                    server-side: {verification.reason}
                  </span>
                </div>
              )}

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                <div className="border border-zinc-800 p-3">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Height</div>
                  <div className="font-mono text-xl text-emerald-300 mt-1">{block.height}</div>
                </div>
                <div className="border border-zinc-800 p-3">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Leaves</div>
                  <div className="font-mono text-xl text-zinc-100 mt-1">{block.leaf_count}</div>
                </div>
                <div className="border border-zinc-800 p-3">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Depth of Truth</div>
                  <div className="font-mono text-xl text-sky-300 mt-1">{block.depth_of_truth}</div>
                </div>
                <div className="border border-zinc-800 p-3">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Payload</div>
                  <div className="font-mono text-xl text-zinc-100 mt-1">{(block.size_bytes / 1024).toFixed(1)}<span className="text-[10px] ml-0.5 text-zinc-500">KiB</span></div>
                </div>
              </div>

              {/* Distribution */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
                  <GitBranch className="h-3 w-3" /> Epistemic distribution
                </div>
                <DistributionBar dist={block.epistemic_distribution} />
                <div className="flex flex-wrap gap-3 mt-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  {Object.entries(block.epistemic_distribution || {}).map(([k, v]) => (
                    <span key={k}><span className="text-zinc-100 font-mono">{v}</span> {k.replace("_", " ")}</span>
                  ))}
                </div>
                {block.origin_distribution ? (
                  <div className="mt-3 pt-3 border-t border-zinc-900" data-testid="block-origin-distribution">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5 font-mono">
                      Origin split · v19.30
                    </div>
                    <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.2em]">
                      {Object.entries(block.origin_distribution).map(([k, v]) => (
                        <span key={k} className={
                          k === "submission"
                            ? "text-violet-300"
                            : "text-cyan-300"
                        }>
                          <span className="font-mono">{v}</span> {k}
                          {v > 0 ? "s" : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Leaves */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
                  <ScrollText className="h-3 w-3" /> Leaves · click to fetch inclusion proof
                </div>
                <div className="border border-zinc-800 divide-y divide-zinc-900">
                  {(block.leaves || []).map((lv, i) => {
                    const isOpen = openLeaf === lv.citation_id;
                    return (
                      <div key={lv.citation_id} className={isOpen ? "bg-zinc-900/40" : ""}>
                        <button
                          className="w-full text-left p-3 flex items-start gap-3 hover:bg-zinc-900/40 transition-colors"
                          onClick={() => loadProof(lv.citation_id)}
                          data-testid={`leaf-row-${i}`}
                        >
                          <span className="shrink-0 font-mono text-[10px] text-zinc-500 mt-0.5 w-6">{i}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <OriginBadge leaf={lv} testid={`leaf-origin-${i}`} />
                              {(lv.triangulation_agreeing || []).length > 0 && (
                                <span className="text-[10px] text-zinc-500 font-mono">
                                  {(lv.triangulation_agreeing || []).join(" + ")}
                                </span>
                              )}
                              {lv.grounding_source_count > 0 && (
                                <span className="text-[10px] text-sky-400 font-mono">
                                  {lv.grounding_source_count} src
                                </span>
                              )}
                            </div>
                            <div className="font-mono text-[10.5px] text-zinc-400 mt-1 break-all">
                              leaf {shortHash(lv.leaf_hash, 16)}
                            </div>
                          </div>
                          <div
                            className="shrink-0 flex items-center gap-1.5 mt-0.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DisputeButton
                              targetType="block_leaf"
                              targetId={lv.citation_id}
                              compact
                              testidPrefix={`leaf-dispute-${i}`}
                            />
                            <Hash className="h-3.5 w-3.5 text-zinc-600" />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-3 pb-3 border-t border-zinc-900 bg-[#07070a]">
                            {proofLoading && (
                              <div className="py-4 text-center text-zinc-500 font-mono text-[11px] uppercase tracking-[0.25em]" data-testid="proof-loading">
                                fetching proof…
                              </div>
                            )}
                            {proof && proof.leaf && proof.leaf.citation_id === lv.citation_id && (
                              <>
                                <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                  Inclusion proof · {proof.proof.length} step{proof.proof.length === 1 ? "" : "s"}
                                </div>
                                <ol className="mt-2 space-y-1 font-mono text-[11px]">
                                  {proof.proof.map((step, idx) => (
                                    <li key={idx} className="border-l-2 border-emerald-500/40 pl-2.5 text-zinc-300">
                                      <span className="text-emerald-400 uppercase tracking-[0.2em] text-[9px] mr-2">{step.position}</span>
                                      {shortHash(step.hash, 18)}
                                    </li>
                                  ))}
                                </ol>
                                <ProofVerifier
                                  block={block}
                                  leaf={proof.leaf}
                                  proof={proof.proof}
                                  expectedRoot={proof.merkle_root}
                                />
                                <div className="mt-2 text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                                  {proof.verification_recipe}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="pt-4 border-t border-zinc-900 flex items-center justify-between">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/truth-blocks/${block.id}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-emerald-300 border border-zinc-800 hover:border-emerald-500/50 px-2 py-1"
                  data-testid="raw-block-json-link"
                >
                  <Link2 className="h-3 w-3" /> raw block JSON
                </a>
                <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">id · {block.id}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TruthCompilerPanel({ isArchitect }) {
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [recall, setRecall] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getTruthCompilerStats()
      .then((s) => { if (!cancelled) setStats(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const onRecall = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;
    setBusy(true);
    try {
      const r = await truthCompilerRecall(query, 5);
      setRecall(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recall failed");
    } finally {
      setBusy(false);
    }
  };

  const onBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await truthCompilerBackfill();
      toast.success(`Indexed ${r.indexed} · skipped ${r.skipped} · errors ${r.errors}`);
      const s = await getTruthCompilerStats();
      setStats(s);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <section className="mb-8 border border-sky-500/30 bg-[#0a0d12] shadow-[0_0_40px_-16px_rgba(56,189,248,0.4)]" data-testid="truth-compiler-panel">
      <div className="p-4 sm:p-5 border-b border-sky-500/20">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-sky-400 mb-1 flex items-center gap-2">
              <Anchor className="h-3 w-3" /> △ Truth Compiler · pre-LLM retrieval
            </div>
            <h2 className="font-heading text-xl sm:text-2xl tracking-[0.1em] text-zinc-100">
              Anchored Truths
            </h2>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
              Every chat message is semantically searched against the verified-citation
              index BEFORE the LLM is called. Top hits are injected into the system
              prompt as anchors the model must defer to over its training data.{" "}
              <span className="text-sky-300">This is what kills drift on known facts.</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Indexed</div>
              <div className="font-mono text-2xl text-sky-300">{stats?.total_indexed ?? "…"}</div>
            </div>
            {isArchitect && (
              <button
                onClick={onBackfill}
                disabled={backfilling}
                data-testid="truth-compiler-backfill"
                className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-40"
                title="Re-index every eligible citation from Mongo"
              >
                <Database className="h-3 w-3" />
                {backfilling ? "Indexing…" : "Backfill"}
              </button>
            )}
          </div>
        </div>

        {stats && (
          <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
            <span>statuses · <span className="text-zinc-300 normal-case">{(stats.anchor_statuses || []).join(" + ")}</span></span>
            <span>mongo eligible · <span className="text-zinc-300">{stats.eligible_in_mongo}</span></span>
            <span className={`${stats.index_drift === 0 ? "text-emerald-300" : "text-amber-300"}`}>
              drift · {stats.index_drift}
            </span>
            <span>top-k cap · <span className="text-zinc-300">{stats.max_anchors_injected}</span></span>
          </div>
        )}
      </div>

      <form onSubmit={onRecall} className="p-4 sm:p-5 border-b border-sky-500/10 flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Preview: what facts would anchor for this query?"
          data-testid="truth-compiler-query"
          className="flex-1 bg-[#08090c] border border-zinc-800 focus:border-sky-500/60 px-3 py-2 text-sm text-zinc-100 font-mono outline-none"
        />
        <button
          type="submit"
          disabled={busy || !query.trim()}
          data-testid="truth-compiler-recall-btn"
          className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 disabled:opacity-40"
        >
          <Search className="h-3.5 w-3.5" />
          {busy ? "…" : "Recall"}
        </button>
      </form>

      {recall && (
        <div className="p-4 sm:p-5" data-testid="truth-compiler-results">
          {(recall.hits || []).length === 0 ? (
            <div className="text-zinc-500 text-[11px] uppercase tracking-[0.25em] font-mono">
              No anchors would fire — the question isn&apos;t covered by any verified fact yet.
            </div>
          ) : (
            <ol className="space-y-2">
              {(recall.hits || []).map((h, i) => (
                <li key={h.citation_id} className="border border-zinc-800 p-3 bg-[#08090c]" data-testid={`anchor-hit-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 font-mono text-[10px] text-zinc-500 mt-0.5 w-5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border ${
                          h.epistemic_status === "fact"
                            ? "border-emerald-500/40 text-emerald-300"
                            : "border-sky-500/40 text-sky-300"
                        }`}>
                          {h.epistemic_status === "fact" ? "✓ fact" : "🌐 web-cited"}
                        </span>
                        {(h.agreeing_models || []).length > 0 && (
                          <span className="text-[10px] text-zinc-400 font-mono">{h.agreeing_models.join(" + ")}</span>
                        )}
                        {h.truth_block && (
                          <span className="text-[10px] text-emerald-400 font-mono" title={h.truth_block.hash}>
                            block #{h.truth_block.height}
                          </span>
                        )}
                        <span className="text-[10px] text-zinc-500 font-mono ml-auto">
                          d={Number(h.distance || 0).toFixed(3)}
                        </span>
                      </div>
                      <p className="font-serif italic text-zinc-100 text-[13.5px] leading-snug">&ldquo;{h.claim}&rdquo;</p>
                      {(h.sources || []).slice(0, 1).map((s, j) => (
                        <a
                          key={j}
                          href={s.uri}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="block mt-1 text-[10px] uppercase tracking-[0.2em] text-sky-400 hover:text-sky-300 truncate"
                        >
                          source · {s.title || s.uri}
                        </a>
                      ))}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

export default function TruthBlocks() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null);
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [sealing, setSealing] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      listTruthBlocks({ limit: 50 }),
      getTruthBlocksPending(),
      verifyTruthChain(),
    ])
      .then(([blocks, p, c]) => {
        if (cancelled) return;
        setData(blocks);
        setPending(p);
        setChain(c);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshTick]);

  const blocks = data?.blocks || [];
  const tip = data?.tip;

  const summary = useMemo(() => {
    const totalLeaves = blocks.reduce((a, b) => a + (b.leaf_count || 0), 0);
    const totalDoT = blocks.reduce((a, b) => a + (b.depth_of_truth || 0), 0);
    return { totalLeaves, totalDoT };
  }, [blocks]);

  const onSeal = async () => {
    setSealing(true);
    try {
      const res = await sealTruthBlock();
      if (res.sealed) {
        toast.success(`Block #${res.block.height} sealed · ${res.leaf_count} leaves · ${res.depth_of_truth} DoT`);
      } else {
        toast.info(res.reason || "No pending leaves");
      }
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seal failed");
    } finally {
      setSealing(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#09090b] text-zinc-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-emerald-400 mb-1 inline-flex items-center gap-2">
              <Blocks className="h-3 w-3" /> △ Truth Chain
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.15em] text-zinc-100">
              The chain
            </h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
              Every verified fact and refuted lie flows through the same pipeline
              below. Individual truths land in the feed, wait in the pending
              queue, and get sealed into a permanent Merkle-signed block every
              256 leaves. Between blocks a{" "}
              <code className="text-emerald-300 text-xs px-1 border border-zinc-800">previous_hash</code>{" "}
              chain anyone can walk.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="/truths"
              data-testid="link-truths"
              className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              title="View individual truths (feed)"
            >
              <ScrollText className="h-3.5 w-3.5" />
              Feed
            </a>
            <button
              onClick={() => setRefreshTick((t) => t + 1)}
              data-testid="truth-blocks-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-emerald-300"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            {user?.is_architect && (
              <button
                onClick={onSeal}
                disabled={sealing || (pending?.pending_count || 0) < 32}
                data-testid="seal-block-btn"
                className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-emerald-500/60 text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/15 disabled:opacity-40 disabled:cursor-not-allowed"
                title={(pending?.pending_count || 0) < 32 ? "Wait until at least 32 leaves accumulate to avoid tiny blocks" : "Seal pending leaves"}
              >
                <Lock className="h-3.5 w-3.5" />
                {sealing ? "Sealing…" : `Seal · ${pending?.pending_count ?? 0}`}
              </button>
            )}
          </div>
        </div>

        {/* Pipeline — Feed → Pending → Sealed */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3" data-testid="chain-pipeline">
          <a
            href="/truths"
            className="border border-emerald-500/30 bg-[#0b0b0d] p-4 hover:border-emerald-500/60 transition-colors group"
            data-testid="pipeline-feed"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-400">
              <ScrollText className="h-3 w-3" /> 1 · Feed
            </div>
            <div className="text-zinc-300 text-sm mt-2">
              Individual verified truths & refuted lies. Public + disputable.
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono text-2xl text-emerald-300">{summary.totalLeaves + (pending?.pending_count || 0)}</span>
              <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">total facts →</span>
            </div>
          </a>

          <div
            className="border border-amber-500/30 bg-[#0b0b0d] p-4"
            data-testid="pipeline-pending"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-400">
              <Layers className="h-3 w-3" /> 2 · Pending
            </div>
            <div className="text-zinc-300 text-sm mt-2">
              Verified but not yet sealed. Auto-seal fires at 256 leaves.
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono text-2xl text-amber-300">
                {pending?.pending_count ?? 0}
                <span className="text-zinc-600 text-sm">/{pending?.auto_seal_at ?? 256}</span>
              </span>
              <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">queued →</span>
            </div>
          </div>

          <div
            className={`border p-4 bg-[#0b0b0d] ${chain?.chain?.valid ? "border-sky-500/40" : "border-rose-500/50"}`}
            data-testid="pipeline-sealed"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-sky-400">
              <Lock className="h-3 w-3" /> 3 · Sealed
            </div>
            <div className="text-zinc-300 text-sm mt-2">
              Merkle-signed blocks · HMAC previous_hash chain · permanent.
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono text-2xl text-sky-300">{data?.count ?? 0}</span>
              <span className={`text-[10px] font-mono ${chain?.chain?.valid ? "text-emerald-400" : "text-rose-400"}`}>
                {chain ? (chain.chain.valid ? "verified ✓" : "broken ✗") : "…"}
              </span>
            </div>
          </div>
        </div>

        {/* Tip banner (compact — no duplicate pending count) */}
        {tip && (
          <div className="mb-6 border border-zinc-800 bg-[#0b0b0d] p-3 flex items-center gap-3" data-testid="chain-tip">
            <Sparkles className="h-4 w-4 text-emerald-400 shrink-0" />
            <div className="text-[11px] text-zinc-400 font-mono">
              Tip of chain · Block #{tip.height} · root {shortHash(tip.merkle_root, 14)}
            </div>
          </div>
        )}

        {/* Truth Compiler — pre-LLM anchor retrieval surface (sidecar, not part of the chain) — moved below the blocks list */}

        {/* Body */}
        {loading && !data && (
          <div className="text-zinc-500 text-sm py-12 text-center font-mono uppercase tracking-[0.25em]" data-testid="truth-blocks-loading">
            loading chain…
          </div>
        )}
        {error && (
          <div className="text-rose-400 text-sm py-6 border border-rose-500/40 bg-rose-500/5 px-4 font-mono" data-testid="truth-blocks-error">
            {String(error)}
          </div>
        )}
        {!loading && !error && blocks.length === 0 && (
          <div className="text-zinc-500 text-sm py-16 text-center border border-zinc-800 bg-[#0b0b0d]" data-testid="truth-blocks-empty">
            <div className="font-heading text-lg text-zinc-300 mb-2">No blocks sealed yet</div>
            <p className="text-xs uppercase tracking-[0.25em]">
              Triangulate some citations on the Ledger, then ask the Architect to seal Genesis.
            </p>
          </div>
        )}
        {blocks.length > 0 && (
          <div className="space-y-3" data-testid="truth-blocks-list">
            {blocks.map((b) => (
              <BlockCard key={b.id} block={b} onOpen={setOpenId} />
            ))}
          </div>
        )}

        {/* SIDECAR — Truth Compiler (semantic index) lives ALONGSIDE the
            chain, not inside it. Architect-only: this is internal ML
            plumbing (the anchor index we inject into LLM prompts), not
            user-facing content. */}
        {user?.is_architect && (
          <>
            <div className="mt-10 mb-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-zinc-900" />
              <div className="text-[9px] uppercase tracking-[0.4em] text-zinc-600 font-mono">
                sidecar system · architect only
              </div>
              <div className="h-px flex-1 bg-zinc-900" />
            </div>
            <TruthCompilerPanel isArchitect />
          </>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-zinc-900 text-[10px] uppercase tracking-[0.3em] text-zinc-600 leading-loose">
          <div>{data?.notes?.scope || "Public auditor surface. No PII, no operator content."}</div>
          <div className="mt-1">{data?.notes?.verification}</div>
        </div>
      </div>

      {openId && <BlockDetail blockId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
